/**
 * Embed theme tokens — the shared half of the store design system.
 *
 * Canonical source of truth for token names, CSS variables, validation, and
 * presets (see docs/store-design-architecture.md). Values are typed primitives
 * validated against allow-list patterns; raw CSS is never accepted.
 */

export interface FwThemeTokens {
    accent?: string;
    accentHover?: string;
    accentContrast?: string;
    accentSoft?: string;
    surface?: string;
    surfaceAlt?: string;
    text?: string;
    muted?: string;
    border?: string;
    radius?: string;
    radiusSm?: string;
    font?: string;
    shadow?: string;
}

export type FwThemeOption = string | (FwThemeTokens & { preset?: string });

type TokenType = 'color' | 'length' | 'font' | 'shadow';

const TOKEN_DEFINITIONS: Record<keyof FwThemeTokens, { cssVar: string; type: TokenType }> = {
    accent:         {cssVar: '--fw-accent',          type: 'color'},
    accentHover:    {cssVar: '--fw-accent-hover',    type: 'color'},
    accentContrast: {cssVar: '--fw-accent-contrast', type: 'color'},
    accentSoft:     {cssVar: '--fw-accent-soft',     type: 'color'},
    surface:        {cssVar: '--fw-surface',         type: 'color'},
    surfaceAlt:     {cssVar: '--fw-surface-alt',     type: 'color'},
    text:           {cssVar: '--fw-text',            type: 'color'},
    muted:          {cssVar: '--fw-muted',           type: 'color'},
    border:         {cssVar: '--fw-border',          type: 'color'},
    radius:         {cssVar: '--fw-radius',          type: 'length'},
    radiusSm:       {cssVar: '--fw-radius-sm',       type: 'length'},
    font:           {cssVar: '--fw-font',            type: 'font'},
    shadow:         {cssVar: '--fw-shadow',          type: 'shadow'},
};

const VALUE_PATTERNS: Record<TokenType, RegExp> = {
    color:  /^(#[0-9a-f]{3,8}|(rgb|hsl)a?\(\s*[\d.,%\s/]+\s*\)|[a-z]{3,25})$/i,
    length: /^\d{1,3}(\.\d+)?(px|rem|em|%)$/,
    font:   /^[\w\s,'"–-]{1,120}$/,
    shadow: /^[\d\s.,()#%a-z-]{1,160}$/i,
};

/**
 * The built-in fallback values baked into fleetwire.css `var()` defaults —
 * i.e. what the 'light' preset looks like. Used by editors/previews that need
 * concrete values for unset tokens.
 */
export const FW_THEME_FALLBACKS: Required<FwThemeTokens> = {
    accent: '#00bd8e',
    accentHover: '#009e74',
    accentContrast: '#ffffff',
    accentSoft: 'rgba(0,189,142,.1)',
    surface: '#ffffff',
    surfaceAlt: '#f7fafa',
    text: '#213b47',
    muted: '#7b8b93',
    border: '#dfe5e8',
    radius: '10px',
    radiusSm: '6px',
    font: 'inherit',
    shadow: '0 12px 32px rgba(0,35,57,.16)',
};

/**
 * Named token bundles. A preset is a starting point — explicit tokens override it.
 * 'light' is the built-in fallback set in fleetwire.css, so it is intentionally empty.
 */
export const FW_THEME_PRESETS: Record<string, FwThemeTokens> = {
    light: {},
    dark: {
        surface: '#16181b',
        surfaceAlt: '#1f2226',
        text: '#e9edef',
        muted: '#93a0a8',
        border: '#2b3036',
        accentSoft: 'rgba(0,191,147,.16)',
        shadow: '0 12px 32px rgba(0,0,0,.45)',
    },
    luxury: {
        accent: '#c9a24b',
        accentHover: '#b8913a',
        accentContrast: '#0b0b0d',
        accentSoft: 'rgba(201,162,75,.14)',
        surface: '#141416',
        surfaceAlt: '#1b1b1e',
        text: '#f5f1e8',
        muted: '#8a867f',
        border: '#2c2c30',
        radius: '0px',
        radiusSm: '0px',
        shadow: '0 24px 60px rgba(0,0,0,.5)',
    },
    minimal: {
        accent: '#1a1a1a',
        accentHover: '#3a3a3a',
        accentContrast: '#ffffff',
        accentSoft: 'rgba(0,0,0,.06)',
        surfaceAlt: '#f6f6f6',
        text: '#111111',
        muted: '#767676',
        border: '#e5e5e5',
        radius: '2px',
        radiusSm: '2px',
        shadow: '0 8px 24px rgba(0,0,0,.1)',
    },
    retro: {
        accent: '#e07a3d',
        accentHover: '#c9652b',
        accentContrast: '#fff8f0',
        accentSoft: 'rgba(224,122,61,.16)',
        surface: '#faf6ef',
        surfaceAlt: '#f0e6d8',
        text: '#3d2b1f',
        muted: '#8a7360',
        border: '#e0d2c0',
        radius: '16px',
        radiusSm: '10px',
        font: 'Georgia, "Times New Roman", serif',
        shadow: '0 16px 40px rgba(61,43,31,.14)',
    },
    coastal: {
        accent: '#1f7a8c',
        accentHover: '#186575',
        accentContrast: '#ffffff',
        accentSoft: 'rgba(31,122,140,.12)',
        surface: '#ffffff',
        surfaceAlt: '#f0f7fa',
        text: '#1b3a4b',
        muted: '#6b8a99',
        border: '#d0e3eb',
        radius: '12px',
        radiusSm: '8px',
        shadow: '0 12px 32px rgba(27,58,75,.12)',
    },
};

/**
 * Resolves a theme option (preset name, token object, or preset + overrides)
 * into a validated map of CSS variable → value. Unknown tokens and invalid
 * values are dropped silently so a typo can never break a customer's page.
 */
export function resolveTheme(option: FwThemeOption | undefined | null): Record<string, string> {
    if (!option) {
        return {};
    }

    let tokens: FwThemeTokens = {};

    if (typeof option === 'string') {
        tokens = FW_THEME_PRESETS[option.toLowerCase()] ?? {};
    } else if (typeof option === 'object') {
        const preset = option.preset ? (FW_THEME_PRESETS[option.preset.toLowerCase()] ?? {}) : {};
        tokens = {...preset, ...option};
    }

    const resolved: Record<string, string> = {};

    for (const [name, definition] of Object.entries(TOKEN_DEFINITIONS)) {
        const raw = tokens[name as keyof FwThemeTokens];

        if (typeof raw !== 'string') {
            continue;
        }

        const value = raw.trim();

        if (VALUE_PATTERNS[definition.type].test(value)) {
            resolved[definition.cssVar] = value;
        }
    }

    return resolved;
}

/**
 * Google-Fonts specs for the font stacks the editor offers, keyed by the first
 * family (lowercased). Only these are auto-loaded; system stacks need nothing.
 */
const LOADABLE_FONTS: Record<string, string> = {
    inter: 'Inter:wght@400;500;600;700',
    poppins: 'Poppins:wght@400;500;600;700',
    marcellus: 'Marcellus',
};

/**
 * Injects a Google-Fonts stylesheet for a recognized font stack so the chosen
 * font actually renders (otherwise it silently falls back). No-op for system
 * stacks, `inherit`, or unknown families. On embeds this hits the host page's
 * CSP; if blocked, the font simply falls back — never fatal.
 */
export function ensureFontLoaded(fontStack: string | undefined | null): void {
    if (!fontStack || fontStack === 'inherit' || typeof document === 'undefined') {
        return;
    }

    const first = fontStack.split(',')[0].trim().replace(/['"]/g, '').toLowerCase();
    const spec = LOADABLE_FONTS[first];
    if (!spec) {
        return;
    }

    const id = `fw-font-${first}`;
    if (document.getElementById(id)) {
        return;
    }

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${spec}&display=swap`;
    document.head.appendChild(link);
}

/**
 * Applies a theme to the page as inline custom properties. Later sources win
 * by re-applying — precedence is documented in docs/store-design-architecture.md.
 */
export function applyEmbedTheme(option: FwThemeOption | undefined | null, root: HTMLElement = document.documentElement): void {
    const variables = resolveTheme(option);

    for (const [cssVar, value] of Object.entries(variables)) {
        root.style.setProperty(cssVar, value);
    }

    ensureFontLoaded(variables['--fw-font']);
}

/**
 * The stored store theme with a different preset, keeping the host's other
 * customizations (accent, font, token overrides). "light" is the default and is
 * stored as no preset; a theme with nothing left in it is stored as null, the
 * same shape the /themes editor saves.
 */
export function withThemePreset(stored: Record<string, string> | null | undefined, preset: string): Record<string, string> | null {
    const next: Record<string, string> = {...(stored ?? {})};

    if (preset && preset !== 'light') {
        next.preset = preset;
    } else {
        delete next.preset;
    }

    return Object.keys(next).length ? next : null;
}
